import { formatDate, formatTimeInterval } from "@chef/helpers";

import { TimeblocksByRangeWithGeoQuery } from "../graphql/generated";

type Timeblock =
  TimeblocksByRangeWithGeoQuery["timeblocksByRangeWithGeo"]["weeks"][number]["timeblocks"][number];

export const groupTimeblocksDataByWeekday = (
  timeblocksData: TimeblocksByRangeWithGeoQuery["timeblocksByRangeWithGeo"]["weeks"][0]["timeblocks"],
) => {
  if (!timeblocksData) {
    return [];
  }

  const groupedByWeekday = timeblocksData.reduce(
    (acc: { [key: number]: typeof timeblocksData }, timeblock) => {
      const weekday = timeblock.weekday;

      if (!acc[weekday]) {
        acc[weekday] = [];
      }

      acc[weekday].push(timeblock);

      return acc;
    },
    {} as { [key: number]: typeof timeblocksData },
  );

  return Object.entries(groupedByWeekday).map(([weekday, timeblocks]) => ({
    weekday: Number(weekday),
    timeblocks,
  }));
};

export const filterAvailableWeeklyTimeblocks = (
  weeks: TimeblocksByRangeWithGeoQuery["timeblocksByRangeWithGeo"]["weeks"],
) => {
  const firstAvailableIdx = weeks.findIndex(({ timeblocks }) =>
    timeblocks.some((t) => !(t.noDelivery && t.fallbackTimeblockId === null)),
  );

  return weeks.slice(firstAvailableIdx, firstAvailableIdx + 4);
};

/**
 * A valid timeblock is one that:
 * - is not a fallback (i.e., `fallbackTimeblockId` is null or undefined)
 * - allows delivery (i.e., `noDelivery` is false)
 *
 * When there are fallback timeblocks, the backend inserts them into the array.
 * These fallback blocks typically have `noDelivery: true` and need to be excluded.
 * @param t - The timeblock to evaluate
 * @returns True if the timeblock is valid, false otherwise
 */
const isValidTimeblock = (t: Timeblock): boolean =>
  !t.fallbackTimeblockId && !t.noDelivery;

/**
 * Returns the list of valid, deliverable timeblocks from the first week that contains them.
 *
 * @param weeks - An array of weeks, each containing a list of timeblocks
 * @returns An array of timeblocks from the first week that has at least one valid timeblock.
 *          Returns an empty array if no valid timeblocks are found in any week.
 */
export const getFirstAvailableTimeblocks = (
  weeks: TimeblocksByRangeWithGeoQuery["timeblocksByRangeWithGeo"]["weeks"],
) => {
  const week = weeks.find((w) => w.timeblocks.some(isValidTimeblock));

  return week?.timeblocks?.filter(isValidTimeblock) ?? [];
};

/**
 * Deduplicates timeblocks based on the following conditions:
 * - Timeblocks with the same `timeblockId` are considered duplicates.
 * - Timeblocks with the same `fallbackTimeblockId` (when `fallbackTimeblockId` is not null) are considered duplicates.
 *
 * @param timeblocks - An array of timeblocks to deduplicate
 * @returns A new array of deduplicated timeblocks
 */
export const deduplicateTimeblocks = (timeblocks: Timeblock[]) => {
  const deduplicatedTimeblocks: Timeblock[] = [];

  for (const timeblock of timeblocks) {
    if (timeblock.noDelivery && timeblock.fallbackTimeblockId === null) {
      continue;
    }

    const idx = deduplicatedTimeblocks.findIndex((t) => {
      if (t.timeblockId === timeblock.timeblockId) {
        return true;
      }

      if (timeblock.fallbackTimeblockId) {
        return t.fallbackTimeblockId === timeblock.fallbackTimeblockId;
      }

      return false;
    });

    if (idx === -1) {
      deduplicatedTimeblocks.push(timeblock);
    }
  }

  if (deduplicatedTimeblocks.length > 2) {
    return deduplicatedTimeblocks.filter((t) =>
      t.fallbackTimeblockId ? false : !t.noDelivery,
    );
  }

  return deduplicatedTimeblocks;
};

/**
 * Creates an array of options from a list of timeblocks, where each option contains:
 * - `value`: The `fallbackTimeblockId` if available, otherwise the `timeblockId`.
 * - `label`: A formatted string combining the delivery date and time interval.
 *
 * The timeblocks are sorted chronologically based on their delivery date and start time.
 *
 * @param args - An object containing the `timeblocks` array to process.
 * @returns An array of options, each with `value` and `label` properties.
 */
export const createTimeblocksOptions = (args: { timeblocks: Timeblock[] }) => {
  const { timeblocks } = args;
  if (!timeblocks || timeblocks.length === 0) {
    return [];
  }

  return timeblocks
    .sort((a, b) => {
      const aDate = new Date(a.deliveryDate);
      aDate.setHours(+a.from.slice(0, 2));

      const bDate = new Date(b.deliveryDate);
      bDate.setHours(+b.from.slice(0, 2));

      return aDate.getTime() - bDate.getTime();
    })
    .map((t) => ({
      value: t.fallbackTimeblockId || t.timeblockId,
      label: `${formatDate(
        new Date(t.deliveryDate),
        "EEEE d. MMM",
      )} ${formatTimeInterval(t.from, t.to, "m")}`,
    }));
};
